import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import PlaceIcon from '@mui/icons-material/Place';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CsrStatus, TripWithTenant } from '../../../../types';
import { csrStatusColors, csrStatusValues } from '../../../../utils/tripTypeHelpers';

export interface ScheduleBulkActionBarProps {
  selectedTrips: TripWithTenant[];
  hangars: Array<{ id: string; name: string }>;
  ramps: Array<{ id: string; name: string }>;
  onBulkStatusChange: (status: CsrStatus) => Promise<void>;
  onBulkDelete: () => void; // Opens confirmation dialog
  onBulkCancel: () => void; // Opens confirmation dialog
  onBulkAssignLocation: (
    locationId: string,
    locationType: 'hangar' | 'ramp',
    locationName: string
  ) => Promise<void>;
  onClearSelection: () => void;
  isProcessing?: boolean;
  canDelete?: boolean;
}

/**
 * Floating action bar for bulk operations on selected trips.
 * Follows Linear/Notion pattern with dark theme, positioned at bottom center.
 */
export function ScheduleBulkActionBar({
  selectedTrips,
  hangars,
  ramps,
  onBulkStatusChange,
  onBulkDelete,
  onBulkCancel,
  onBulkAssignLocation,
  onClearSelection,
  isProcessing = false,
  canDelete = true,
}: ScheduleBulkActionBarProps) {
  const { t } = useTranslation();
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null);

  if (selectedTrips.length === 0) return null;

  const getStatusLabel = (status: CsrStatus): string => {
    return t(`screens.schedule.scheduleList.filters.csrStatusOptions.${status}`);
  };

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1300,
        px: 3,
        py: 1.5,
        borderRadius: 3,
        bgcolor: '#1a1a2e', // Dark bar like Linear/OperationsInbox
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        minWidth: 500,
        maxWidth: '90vw',
        flexWrap: 'wrap',
      }}
    >
      {/* Selection count */}
      <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
        {t('screens.schedule.scheduleList.bulkActions.selected', {
          count: selectedTrips.length,
        })}
      </Typography>

      <Divider
        orientation="vertical"
        flexItem
        sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
      />

      {/* CSR Status Change */}
      <Button
        size="small"
        startIcon={<EditIcon />}
        onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
        disabled={isProcessing}
        sx={{ color: 'white', textTransform: 'none' }}
      >
        {t('screens.schedule.scheduleList.bulkActions.setStatus')}
      </Button>
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
        sx={{ zIndex: 1400 }}
      >
        {csrStatusValues.map((status) => {
          const colors = csrStatusColors[status];
          return (
            <MenuItem
              key={status}
              onClick={async () => {
                setStatusMenuAnchor(null);
                await onBulkStatusChange(status);
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '3px',
                    bgcolor: colors.text,
                  }}
                />
                <Typography sx={{ fontSize: '0.875rem', color: colors.text, fontWeight: 500 }}>
                  {getStatusLabel(status)}
                </Typography>
              </Box>
            </MenuItem>
          );
        })}
      </Menu>

      {/* Location Assignment */}
      {showLocationPicker ? (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {hangars.slice(0, 3).map((h) => (
            <Button
              key={h.id}
              size="small"
              variant="outlined"
              onClick={() => {
                setShowLocationPicker(false);
                onBulkAssignLocation(h.id, 'hangar', h.name);
              }}
              disabled={isProcessing}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.3)',
                textTransform: 'none',
                fontSize: 11,
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.5)',
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              {h.name}
            </Button>
          ))}
          {ramps.slice(0, 2).map((r) => (
            <Button
              key={r.id}
              size="small"
              variant="outlined"
              onClick={() => {
                setShowLocationPicker(false);
                onBulkAssignLocation(r.id, 'ramp', r.name);
              }}
              disabled={isProcessing}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.3)',
                textTransform: 'none',
                fontSize: 11,
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.5)',
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              {r.name}
            </Button>
          ))}
          <Button
            size="small"
            onClick={() => setShowLocationPicker(false)}
            sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 'auto', px: 1 }}
          >
            <CloseIcon fontSize="small" />
          </Button>
        </Box>
      ) : (
        <Button
          size="small"
          startIcon={<PlaceIcon />}
          onClick={() => setShowLocationPicker(true)}
          disabled={isProcessing}
          sx={{ color: 'white', textTransform: 'none' }}
        >
          {t('screens.schedule.scheduleList.bulkActions.assignLocation')}
        </Button>
      )}

      {canDelete && (
        <>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
          />

          {/* Cancel (soft delete) */}
          <Button
            size="small"
            startIcon={<CancelIcon />}
            onClick={onBulkCancel}
            disabled={isProcessing}
            sx={{ color: '#fca5a5', textTransform: 'none' }}
          >
            {t('screens.schedule.scheduleList.bulkActions.cancel')}
          </Button>

          {/* Delete (hard delete) */}
          <Button
            size="small"
            startIcon={<DeleteForeverIcon />}
            onClick={onBulkDelete}
            disabled={isProcessing}
            sx={{ color: '#ef4444', textTransform: 'none' }}
          >
            {t('screens.schedule.scheduleList.bulkActions.delete')}
          </Button>
        </>
      )}

      {/* Processing indicator */}
      {isProcessing && <CircularProgress size={20} sx={{ color: 'white' }} />}

      {/* Clear selection */}
      <Box sx={{ ml: 'auto' }}>
        <Button
          size="small"
          onClick={onClearSelection}
          disabled={isProcessing}
          sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 'auto', p: 0.5 }}
          title={t('screens.schedule.scheduleList.bulkActions.clearSelection')}
        >
          <CloseIcon fontSize="small" />
        </Button>
      </Box>
    </Paper>
  );
}
